<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Filler
} from 'chart.js'
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

const props = defineProps({
  mode:                 String,   // 'projet' | 'estimation'
  projet:               Object,
  estimation:           Object,
  estimations:          Array,
  debut:                String,
  fin:                  String,
  periode_rapports:     Object,
  rapports:             Array,
  graph_data:           Object,
  compilation_base_url: String,
  can_see_costs:        Boolean,
  can_export:           Boolean,
  show_meteo:           Boolean,
})

// ── Dates réactives pour le formulaire ──────────────────────────────────────
const localDebut = ref(props.debut)
const localFin   = ref(props.fin)

const compilationUrl = computed(() =>
  `${props.compilation_base_url}&debut=${localDebut.value}&fin=${localFin.value}`
)

// ── Formatters ──────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(val)
}
function fmtNeg(val) {
  if (val === null || val === undefined) return '—'
  return val < 0 ? `(${fmtCurrency(Math.abs(val))})` : fmtCurrency(val)
}
function fmtBalance(val) {
  if (val === null || val === undefined) return '—'
  return val < 0 ? `(${fmtCurrency(Math.abs(val))})` : fmtCurrency(val)
}
function balanceCls(val) {
  return val >= 0 ? 'text-tertiary' : 'text-error'
}

// ── Recherche, pagination, taille de page ────────────────────────────────────
const searchQuery  = ref('')
const pageSize     = ref(25)
const currentPage  = ref(1)

const filteredRapports = computed(() => {
  const q = searchQuery.value.toLowerCase().trim()
  if (!q) return props.rapports
  return props.rapports.filter(r =>
    r.date.toLowerCase().includes(q) ||
    r.projet_code.toLowerCase().includes(q) ||
    r.estimation_code.toLowerCase().includes(q) ||
    r.nom_complet.toLowerCase().includes(q)
  )
})

const totalPages = computed(() => Math.ceil(filteredRapports.value.length / pageSize.value))

const pagedRapports = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filteredRapports.value.slice(start, start + pageSize.value)
})

const pageNumbers = computed(() => {
  const total = totalPages.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const cur = currentPage.value
  const pages = new Set([1, total, cur])
  if (cur > 1) pages.add(cur - 1)
  if (cur < total) pages.add(cur + 1)
  return [...pages].sort((a, b) => a - b)
})

// Reset page quand filtre ou taille change
function onSearch() { currentPage.value = 1 }
function onPageSize() { currentPage.value = 1 }

// ── Copier / Imprimer ─────────────────────────────────────────────────────────
function copyTable() {
  const rows = filteredRapports.value.map(r => [
    r.projet_code, r.estimation_code, r.date, r.nom_complet
  ].join('\t'))
  navigator.clipboard.writeText(rows.join('\n'))
}

function printTable() {
  window.print()
}

// ── Line chart ───────────────────────────────────────────────────────────────
const hasGraph = computed(() =>
  props.graph_data && Object.keys(props.graph_data).length > 0 &&
  props.graph_data.valeur_execute &&
  Object.keys(props.graph_data.valeur_execute).length > 0
)

const lineLabels = computed(() =>
  hasGraph.value ? Object.keys(props.graph_data.valeur_execute) : []
)

const lineData = computed(() => ({
  labels: lineLabels.value,
  datasets: [
    {
      label: 'Valeur exécutée',
      data: lineLabels.value.map(k => props.graph_data.valeur_execute[k] ?? 0),
      borderColor: '#4ade80',
      backgroundColor: 'rgba(74,222,128,0.08)',
      tension: 0.3, fill: false, pointRadius: 3,
    },
    {
      label: 'Valeur globale',
      data: lineLabels.value.map(k => props.graph_data.valeur_execute_total[k] ?? 0),
      borderColor: '#60a5fa',
      backgroundColor: 'rgba(96,165,250,0.08)',
      tension: 0.3, fill: false, pointRadius: 3,
    },
    {
      label: 'Coûts personnel',
      data: lineLabels.value.map(k => props.graph_data.couts_personnel[k] ?? 0),
      borderColor: '#f97316',
      backgroundColor: 'rgba(249,115,22,0.08)',
      tension: 0.3, fill: false, pointRadius: 3,
    },
    {
      label: 'Coûts équipements',
      data: lineLabels.value.map(k => props.graph_data.couts_equipement[k] ?? 0),
      borderColor: '#a78bfa',
      backgroundColor: 'rgba(167,139,250,0.08)',
      tension: 0.3, fill: false, pointRadius: 3,
    },
    {
      label: 'Productivité',
      data: lineLabels.value.map(k => props.graph_data.productivite[k] ?? 0),
      borderColor: '#f43f5e',
      backgroundColor: 'rgba(244,63,94,0.08)',
      tension: 0.3, fill: false, pointRadius: 3,
    },
  ],
}))

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: '#cbd5e1', boxWidth: 12, font: { size: 11 } } },
    tooltip: { callbacks: {
      label: ctx => ` ${new Intl.NumberFormat('fr-CA', { style:'currency', currency:'CAD', minimumFractionDigits:0 }).format(ctx.raw)}`
    }}
  },
  scales: {
    x: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.1)' } },
    y: { ticks: { color: '#94a3b8', font: { size: 10 },
         callback: v => new Intl.NumberFormat('fr-CA', { notation:'compact', style:'currency', currency:'CAD' }).format(v)
       }, grid: { color: 'rgba(148,163,184,0.1)' } },
  },
}
</script>

<template>
  <AppLayout>
    <div class="p-6 space-y-6">

      <!-- ── Topbar ───────────────────────────────────────────────────────── -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <span class="material-symbols-outlined text-primary text-2xl">summarize</span>
          <div>
            <h1 class="text-xl font-semibold text-on-surface">
              Rapports — {{ mode === 'estimation' ? estimation.code : projet.code }}
            </h1>
            <p class="text-sm text-on-surface-variant">
              {{ rapports.length }} rapport{{ rapports.length !== 1 ? 's' : '' }} trouvé{{ rapports.length !== 1 ? 's' : '' }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <a
            v-if="can_export && rapports.length"
            :href="mode === 'estimation'
              ? `/rapports/search.xlsx?estimation_id=${estimation.id}`
              : `/rapports/search.xlsx?projet_id=${projet.id}`"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-outline-variant text-on-surface-variant hover:bg-surface-container-highest text-sm transition-colors"
          >
            <span class="material-symbols-outlined text-base">download</span>
            Export XLSX
          </a>
        </div>
      </div>

      <!-- ── Row 1 : Info Card + Graph ───────────────────────────────────── -->
      <div class="grid gap-6" :class="can_see_costs && hasGraph ? 'grid-cols-2' : 'grid-cols-1 max-w-xl'">

        <!-- Info Card -->
        <div class="bg-surface-container rounded-xl border border-outline-variant p-5 space-y-5">

          <!-- Infos projet/estimation -->
          <div>
            <h2 class="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-3">
              Informations
            </h2>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Projet</span>
                <a :href="`/projets/${projet.id}`" class="text-primary hover:underline font-medium">
                  {{ projet.code }}
                </a>
              </div>
              <div v-if="mode === 'estimation'" class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Estimation</span>
                <a :href="`/estimations/${estimation.id}`" class="text-primary hover:underline font-medium">
                  {{ estimation.code }}
                </a>
              </div>
              <div v-if="mode === 'projet' && estimations.length" class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Estimations</span>
                <div class="flex flex-col items-end gap-1">
                  <a
                    v-for="e in estimations" :key="e.id"
                    :href="`/estimations/${e.id}`"
                    class="text-primary hover:underline font-medium"
                  >{{ e.code }}</a>
                </div>
              </div>
              <div class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Période projet</span>
                <span class="text-on-surface font-medium">{{ projet.debut }} → {{ projet.fin }}</span>
              </div>
              <div v-if="periode_rapports" class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Période rapports</span>
                <span class="text-on-surface font-medium">{{ periode_rapports.debut }} → {{ periode_rapports.fin }}</span>
              </div>
              <div v-else class="flex justify-between gap-4">
                <span class="text-on-surface-variant">Période rapports</span>
                <span class="text-on-surface-variant italic">Aucun rapport</span>
              </div>
            </div>
          </div>

          <!-- Séparateur -->
          <div class="border-t border-outline-variant"></div>

          <!-- Formulaire de recherche -->
          <div>
            <h2 class="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-3">
              Période de recherche
            </h2>
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs text-on-surface-variant mb-1">Début</label>
                  <input
                    v-model="localDebut"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg bg-surface-container-highest border border-outline-variant text-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label class="block text-xs text-on-surface-variant mb-1">Fin</label>
                  <input
                    v-model="localFin"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg bg-surface-container-highest border border-outline-variant text-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              <a
                :href="compilationUrl"
                class="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary text-on-primary font-medium text-sm hover:opacity-90 transition-opacity"
              >
                <span class="material-symbols-outlined text-base">bar_chart</span>
                Voir la compilation
              </a>
            </div>
          </div>
        </div>

        <!-- Line Chart -->
        <div v-if="can_see_costs && hasGraph" class="bg-surface-container rounded-xl border border-outline-variant p-5">
          <h2 class="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-4">
            Évolution sur la période
          </h2>
          <div class="h-64">
            <Line :data="lineData" :options="lineOptions" />
          </div>
        </div>
      </div>

      <!-- ── Row 2 : Table des rapports ──────────────────────────────────── -->
      <div v-if="rapports.length" class="bg-surface-container rounded-xl border border-outline-variant overflow-hidden">
        <!-- Toolbar -->
        <div class="px-6 py-4 border-b border-outline-variant flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <h2 class="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">
              Liste des rapports
              <span class="ml-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
                {{ filteredRapports.length }}
              </span>
            </h2>
            <!-- Taille de page -->
            <div class="flex items-center gap-1.5 text-xs text-on-surface-variant">
              <span>Afficher</span>
              <select
                v-model.number="pageSize"
                @change="onPageSize"
                class="px-2 py-1 rounded-lg bg-surface-container-highest border border-outline-variant text-on-surface text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
              <span>par page</span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <!-- Copier -->
            <button
              @click="copyTable"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-outline-variant text-on-surface-variant hover:bg-surface-container-highest text-xs transition-colors"
              title="Copier dans le presse-papiers"
            >
              <span class="material-symbols-outlined text-base">content_copy</span>
              Copier
            </button>
            <!-- Imprimer -->
            <button
              @click="printTable"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-outline-variant text-on-surface-variant hover:bg-surface-container-highest text-xs transition-colors"
              title="Imprimer"
            >
              <span class="material-symbols-outlined text-base">print</span>
              Imprimer
            </button>
            <!-- Recherche -->
            <div class="relative">
              <span class="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-base text-on-surface-variant pointer-events-none">search</span>
              <input
                v-model="searchQuery"
                @input="onSearch"
                type="text"
                placeholder="Rechercher..."
                class="pl-8 pr-3 py-1.5 rounded-lg bg-surface-container-highest border border-outline-variant text-on-surface text-xs w-44 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-surface-container-low/50 text-on-surface-variant text-xs uppercase tracking-wider">
                <th class="px-4 py-3 text-left font-medium">Projet</th>
                <th class="px-4 py-3 text-left font-medium">Estimation</th>
                <th class="px-4 py-3 text-left font-medium">Date</th>
                <th class="px-4 py-3 text-left font-medium">Utilisateur</th>
                <template v-if="can_see_costs">
                  <th class="px-4 py-3 text-right font-medium">Val. globale</th>
                  <th class="px-4 py-3 text-right font-medium">Val. exécutée</th>
                  <th class="px-4 py-3 text-right font-medium">Personnel</th>
                  <th class="px-4 py-3 text-right font-medium">Équipements</th>
                  <th class="px-4 py-3 text-right font-medium">Productivité</th>
                </template>
                <th v-if="show_meteo" class="px-4 py-3 text-center font-medium">Météo</th>
                <th class="px-4 py-3 text-center font-medium">Voir</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, i) in pagedRapports" :key="r.id"
                :class="['hover:bg-surface-container-highest/20 transition-colors', i % 2 === 1 ? 'bg-surface-container-low/20' : '']"
              >
                <td class="px-4 py-3 text-on-surface-variant">{{ r.projet_code }}</td>
                <td class="px-4 py-3 text-on-surface-variant">{{ r.estimation_code }}</td>
                <td class="px-4 py-3">
                  <a :href="`/rapports/${r.id}`" class="text-primary hover:underline font-medium">
                    {{ r.date }}
                  </a>
                </td>
                <td class="px-4 py-3 text-on-surface">{{ r.nom_complet }}</td>
                <template v-if="can_see_costs">
                  <td class="px-4 py-3 text-right text-on-surface font-mono text-xs">
                    {{ fmtCurrency(r.c_valeur_total) }}
                  </td>
                  <td class="px-4 py-3 text-right text-on-surface font-mono text-xs">
                    {{ fmtCurrency(r.c_valeur) }}
                  </td>
                  <td class="px-4 py-3 text-right text-on-surface font-mono text-xs">
                    {{ fmtNeg(r.c_personnel) }}
                  </td>
                  <td class="px-4 py-3 text-right text-on-surface font-mono text-xs">
                    {{ fmtNeg(r.c_equipement) }}
                  </td>
                  <td class="px-4 py-3 text-right font-mono text-xs" :class="balanceCls(r.c_productivite)">
                    {{ fmtBalance(r.c_productivite) }}
                  </td>
                </template>
                <td v-if="show_meteo" class="px-4 py-3 text-center text-on-surface-variant">
                  {{ r.meteo ?? '—' }}
                </td>
                <td class="px-4 py-3 text-center">
                  <a
                    :href="`/rapports/${r.id}`"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                    title="Voir le rapport"
                  >
                    <span class="material-symbols-outlined text-base">open_in_new</span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-between px-6 py-3 border-t border-outline-variant">
          <p class="text-xs text-on-surface-variant">
            {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredRapports.length) }}
            sur {{ filteredRapports.length }} rapports
          </p>
          <div class="flex items-center gap-1">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="(p, idx) in pageNumbers" :key="p">
              <span
                v-if="idx > 0 && pageNumbers[idx - 1] !== p - 1"
                class="text-on-surface-variant text-xs px-1"
              >…</span>
              <button
                @click="currentPage = p"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-highest'
                ]"
              >{{ p }}</button>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- État vide -->
      <div v-else class="bg-surface-container rounded-xl border border-outline-variant p-12 text-center">
        <span class="material-symbols-outlined text-4xl text-on-surface-variant mb-3 block">inbox</span>
        <p class="text-on-surface-variant">Aucun rapport pour cette période.</p>
      </div>

    </div>
  </AppLayout>
</template>
